import { apiClient } from '@api'

/**
 * Project Document Service
 *
 * Handles document uploads and management for projects
 */

const projectDocumentService = {
	/**
	 * Get all documents for a project
	 * @param {number} projectId - Project ID
	 * @returns {Promise} Documents list
	 */
	async getByProject(projectId) {
		return apiClient.get(`/project-documents?project_id=${projectId}`)
	},

	/**
	 * Get document by ID
	 * @param {number} id - Document ID
	 * @returns {Promise} Document data
	 */
	async getById(id) {
		return apiClient.get(`/project-documents/${id}`)
	},

	/**
	 * Upload document
	 * @param {File} file - File to upload
	 * @param {Object} metadata - Document metadata
	 * @param {Function} onProgress - Progress callback
	 * @returns {Promise} Uploaded document
	 */
	async upload(file, metadata, onProgress = null) {
		const { projectId, documentType, uploadedBy, description } = metadata

		return apiClient.uploadFile(
			'/project-documents/upload',
			file,
			{
				project_id: projectId,
				document_type: documentType || 'other',
				uploaded_by: uploadedBy,
				description: description || '',
			},
			onProgress
		)
	},

	/**
	 * Delete document
	 * @param {number} id - Document ID
	 * @returns {Promise} Deletion result
	 */
	async delete(id) {
		return apiClient.delete(`/project-documents/${id}`)
	},

	/**
	 * Get download URL for document
	 * @param {number} id - Document ID
	 * @returns {string} Download URL
	 */
	getDownloadUrl(id) {
		return `${apiClient.baseURL}/project-documents/${id}/download`
	},

	/**
	 * Get document statistics for a project
	 * @param {number} projectId - Project ID
	 * @returns {Promise} Statistics data
	 */
	async getStats(projectId) {
		return apiClient.get(`/projects/${projectId}/documents/stats`)
	},

	/**
	 * Get all documents for a project (project + checklist documents)
	 * Organized by type:
	 * - project_documents: Files uploaded while creating the project
	 * - checklist_documents: Files uploaded against checklist tasks
	 * - checklist_grouped: Checklist documents grouped by certificate/standard
	 *
	 * @param {number} projectId - Project ID
	 * @returns {Promise} All documents organized by type
	 */
	async getAllDocuments(projectId) {
		return apiClient.get(`/projects/${projectId}/all-documents`)
	},

	/**
	 * Validate file before upload
	 * @param {File} file - File to validate
	 * @returns {Object} Validation result { valid, errors }
	 */
	validateFile(file) {
		const errors = []
		const maxSize = 10 * 1024 * 1024 // 10MB
		const allowedTypes = [
			'application/pdf',
			'application/msword',
			'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
		]
		const allowedExtensions = ['.pdf', '.doc', '.docx']

		// Check file size
		if (file.size > maxSize) {
			errors.push(`File size exceeds 10MB limit (${(file.size / 1024 / 1024).toFixed(2)}MB)`)
		}

		// Check MIME type
		if (!allowedTypes.includes(file.type)) {
			const extension = file.name.split('.').pop()?.toLowerCase()
			if (!allowedExtensions.includes(`.${extension}`)) {
				errors.push('Invalid file type. Only PDF, DOC, and DOCX are allowed.')
			}
		}

		return {
			valid: errors.length === 0,
			errors,
		}
	},

	/**
	 * Get document type label
	 * @param {string} type - Document type key
	 * @returns {string} Human-readable label
	 */
	getTypeLabel(type) {
		const labels = {
			msa: 'Master Service Agreement (MSA)',
			sow: 'Statement of Work (SOW)',
			purchase_order: 'Purchase Order',
			contract: 'Contract',
			security_questionnaire: 'Security Questionnaire',
			other: 'Other Document',
		}
		return labels[type] || type
	},

	/**
	 * Get all document types
	 * @returns {Array} Document types with labels
	 */
	getDocumentTypes() {
		return [
			{ value: 'msa', label: 'Master Service Agreement (MSA)' },
			{ value: 'sow', label: 'Statement of Work (SOW)' },
			{ value: 'purchase_order', label: 'Purchase Order' },
			{ value: 'contract', label: 'Contract' },
			{ value: 'security_questionnaire', label: 'Security Questionnaire' },
			{ value: 'other', label: 'Other Document' },
		]
	},

	/**
	 * Format file size for display
	 * @param {number} bytes - Size in bytes
	 * @returns {string} Formatted size
	 */
	formatFileSize(bytes) {
		if (bytes === 0) return '0 Bytes'
		const k = 1024
		const sizes = ['Bytes', 'KB', 'MB', 'GB']
		const i = Math.floor(Math.log(bytes) / Math.log(k))
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
	},
}

export default projectDocumentService
